/**
 * Configuración contable para la exportación a A3ERP.
 *
 * Este sistema no lleva contabilidad; la lleva A3 y aquí se la alimenta.
 * Todo lo de esta pantalla son **datos que sólo conoce quien lleva la
 * contabilidad**: los códigos de IVA y las cuentas los define A3, no
 * nosotros, y por eso se configuran en vez de estar en el código.
 *
 * Lo primero que se ve es lo que falta. Un tipo de IVA en uso y sin código
 * de A3 impide exportar esas facturas, y enterarse al cerrar el mes es
 * tarde: aquí sale con nombre y apellidos desde el primer día.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';

interface Settings {
  chart_of_accounts: string;
  customer_code_numeric: boolean;
  customer_code_max_length: number;
  customer_code_pad_length: number;
  account_length: number;
  account_level: number;
  receivable_root: string;
  payable_root: string;
  fallback_sales_account: string;
}

interface SalesAccount {
  id: string;
  line_kind: string;
  account: string;
  description: string | null;
}

interface VatCode {
  id: string;
  tax_rate: string | null;
  a3_code: string;
  description: string | null;
}

interface Configuration {
  settings: Settings;
  salesAccounts: SalesAccount[];
  vatCodes: VatCode[];
  /** Tipos de IVA en uso y sin código de A3: bloquean la exportación. */
  missingRates: string[];
  /** Tipos de línea en uso y sin cuenta: caen en la de respaldo. */
  missingKinds: string[];
}

const KIND_LABELS: Record<string, string> = {
  PRODUCTO: 'Venta de material',
  SERVICIO: 'Mantenimiento y servicios',
  MANO_OBRA: 'Mano de obra',
  DESPLAZAMIENTO: 'Desplazamientos',
};

const KINDS = ['PRODUCTO', 'SERVICIO', 'MANO_OBRA', 'DESPLAZAMIENTO'] as const;

function rateLabel(rate: string | null): string {
  if (rate === null) return 'Exento / no sujeto';
  return `${Number(rate)} %`;
}

export function AccountingSettings(): React.ReactNode {
  const { api, user } = useSession();
  const canWrite = hasPermission(user, 'platform.update');

  const [data, setData] = useState<Configuration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [rate, setRate] = useState('');
  const [a3Code, setA3Code] = useState('');
  const [kind, setKind] = useState<string>('PRODUCTO');
  const [account, setAccount] = useState('');
  const [numeric, setNumeric] = useState(false);
  const [pad, setPad] = useState('0');

  const load = useCallback(async () => {
    try {
      const cfg = await api.get<Configuration>('/accounting/a3/settings');
      setData(cfg);
      setNumeric(cfg.settings.customer_code_numeric);
      setPad(String(cfg.settings.customer_code_pad_length));
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'No se ha podido cargar la configuración.',
      );
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>, ok: string): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await action();
      setNotice(ok);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido guardar.');
    } finally {
      setBusy(false);
    }
  }

  if (data === null) {
    return error !== null ? (
      <p className="screen__error" role="alert">
        {error}
      </p>
    ) : (
      <p className="table__loading">Cargando…</p>
    );
  }

  return (
    <>
      <p className="screen__note">
        La contabilidad la lleva A3; aquí sólo se le da de comer. Los códigos de IVA y las cuentas
        son los de A3 — cópielos tal cual, porque son ellos los que deciden si la importación
        cuadra.
      </p>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      {/* Lo que falta va primero: es lo que impide exportar. */}
      {data.missingRates.length > 0 && (
        <p className="screen__error" role="alert">
          Hay facturas con {data.missingRates.map((r) => `${Number(r)} %`).join(', ')} y ese tipo no
          tiene código de A3. Esas facturas <strong>no se pueden exportar</strong> hasta que lo
          tenga.
        </p>
      )}
      {data.missingKinds.length > 0 && (
        <p className="screen__note screen__note--strong">
          Sin cuenta configurada para {data.missingKinds.map((k) => KIND_LABELS[k] ?? k).join(', ')}:
          esas líneas irán a la cuenta de respaldo ({data.settings.fallback_sales_account}).
        </p>
      )}

      <section className="form__section">
        <h2 className="form__section-title">Códigos de IVA de A3</h2>
        <p className="form__section-note">
          Uno por cada tipo que factura, más el de exento. El código lo da A3 (por ejemplo
          «ORD21»); el tipo es el porcentaje, o vacío para exentas.
        </p>

        <div className="scroller section__table">
          <table className="table__grid">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Código en A3</th>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {data.vatCodes.map((code) => (
                <tr key={code.id}>
                  <td>{rateLabel(code.tax_rate)}</td>
                  <td>{code.a3_code}</td>
                  {canWrite && (
                    <td>
                      <button
                        className="button button--quiet"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () =>
                              api.request(`/accounting/a3/vat-codes/${code.id}`, { method: 'DELETE' }),
                            'Código eliminado.',
                          )
                        }
                      >
                        Quitar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {data.vatCodes.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 3 : 2} className="table__empty">
                    Ningún código configurado: no se puede exportar nada todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {canWrite && (
          <>
            <div className="field-grid">
              <label>
                Tipo (%)
                <input
                  value={rate}
                  onChange={(event) => setRate(event.target.value)}
                  placeholder="21"
                  inputMode="decimal"
                />
                <small className="field__help">Vacío = exento o no sujeto.</small>
              </label>
              <label>
                Código en A3
                <input
                  value={a3Code}
                  onChange={(event) => setA3Code(event.target.value)}
                  placeholder="ORD21"
                />
              </label>
            </div>
            <button
              className="button button--primary"
              type="button"
              disabled={busy || a3Code.trim() === ''}
              onClick={() =>
                void run(
                  () =>
                    api.post('/accounting/a3/vat-codes', {
                      taxRate: rate.trim() === '' ? null : rate.trim(),
                      a3Code: a3Code.trim(),
                    }),
                  'Código guardado.',
                ).then(() => {
                  setRate('');
                  setA3Code('');
                })
              }
            >
              Añadir código
            </button>
          </>
        )}
      </section>

      <section className="form__section">
        <h2 className="form__section-title">Cuentas de venta</h2>
        <p className="form__section-note">
          Separan la venta de material del mantenimiento. La cuenta se asigna por tipo de línea de
          factura, así que una factura con las dos cosas se parte sola en el fichero.
        </p>

        <div className="scroller section__table">
          <table className="table__grid">
            <thead>
              <tr>
                <th>Concepto</th>
                <th>Cuenta</th>
                {canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {data.salesAccounts.map((row) => (
                <tr key={row.id}>
                  <td>{KIND_LABELS[row.line_kind] ?? row.line_kind}</td>
                  <td>{row.account}</td>
                  {canWrite && (
                    <td>
                      <button
                        className="button button--quiet"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () =>
                              api.request(`/accounting/a3/sales-accounts/${row.id}`, {
                                method: 'DELETE',
                              }),
                            'Cuenta eliminada.',
                          )
                        }
                      >
                        Quitar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {data.salesAccounts.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 3 : 2} className="table__empty">
                    Sin cuentas: todo iría a la de respaldo.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {canWrite && (
          <>
            <div className="field-grid">
              <label>
                Concepto
                <select value={kind} onChange={(event) => setKind(event.target.value)}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Cuenta
                <input
                  value={account}
                  onChange={(event) => setAccount(event.target.value)}
                  placeholder="70500000"
                  inputMode="numeric"
                />
              </label>
            </div>
            <button
              className="button button--primary"
              type="button"
              disabled={busy || account.trim() === ''}
              onClick={() =>
                void run(
                  () =>
                    api.post('/accounting/a3/sales-accounts', {
                      lineKind: kind,
                      account: account.trim(),
                    }),
                  'Cuenta guardada.',
                ).then(() => setAccount(''))
              }
            >
              Añadir cuenta
            </button>
          </>
        )}
      </section>

      <section className="form__section">
        <h2 className="form__section-title">Código de cliente en A3</h2>
        <p className="form__section-note">
          Cuando la ficha del cliente no lleva un código de A3 explícito, se deriva del interno.
          A3 muestra los códigos con ceros por delante («000001»), pero su importación puede
          quererlos sin ellos: depende de la instalación, y por eso se configura aquí en vez de
          estar decidido en el código.
        </p>

        <p className="field__help">
          Ahora mismo un cliente <strong>000001</strong> se exportaría como{' '}
          <strong>
            {(() => {
              const digits = numeric ? '000001'.replace(/[^0-9]/g, '') : '000001';
              const desnudo = digits.replace(/^0+/, '') || '0';
              const n = Number(pad) || 0;
              return n > 0 ? desnudo.padStart(n, '0') : desnudo;
            })()}
          </strong>
          .
        </p>

        {canWrite && (
          <>
            <div className="field-grid">
              <label>
                <span>
                  <input
                    type="checkbox"
                    checked={numeric}
                    onChange={(event) => setNumeric(event.target.checked)}
                  />{' '}
                  Sólo dígitos
                </span>
                <small className="field__help">Descarta las letras al derivar.</small>
              </label>
              <label>
                Ceros por delante
                <input
                  type="number"
                  min="0"
                  max={data.settings.customer_code_max_length}
                  value={pad}
                  onChange={(event) => setPad(event.target.value)}
                />
                <small className="field__help">0 = sin relleno.</small>
              </label>
            </div>
            <button
              className="button button--primary"
              type="button"
              disabled={busy}
              onClick={() =>
                void run(
                  () =>
                    api.request('/accounting/a3/settings', {
                      method: 'PUT',
                      body: JSON.stringify({
                        chartOfAccounts: data.settings.chart_of_accounts,
                        accountLength: data.settings.account_length,
                        accountLevel: data.settings.account_level,
                        receivableRoot: data.settings.receivable_root,
                        payableRoot: data.settings.payable_root,
                        fallbackSalesAccount: data.settings.fallback_sales_account,
                        customerCodeNumeric: numeric,
                        customerCodeMaxLength: data.settings.customer_code_max_length,
                        customerCodePadLength: Number(pad) || 0,
                      }),
                    }),
                  'Formato del código guardado.',
                )
              }
            >
              Guardar formato
            </button>
          </>
        )}
      </section>

      <section className="form__section">
        <h2 className="form__section-title">Formato de las subcuentas</h2>
        <p className="form__section-note">
          Todas las subcuentas de una empresa miden lo mismo en A3. Mezclar 8 y 9 dígitos es el
          error clásico de la primera importación, y no lo detecta nadie hasta que el mayor no
          cuadra. Longitud actual: <strong>{data.settings.account_length}</strong> dígitos · plan{' '}
          <strong>{data.settings.chart_of_accounts}</strong> · clientes desde{' '}
          <strong>{data.settings.receivable_root}</strong> · respaldo de ventas{' '}
          <strong>{data.settings.fallback_sales_account}</strong>.
        </p>
      </section>

    </>
  );
}
